import type { MoodboardItem, FontMeta } from '@backend/lib/db/schema/moodboard'
import type { Point } from './geometry'
import { screenToWorld } from './geometry'
import { classifyUrl } from '@backend/lib/util/classifyUrl'

// Default sizes per item type. Used when spawning new items so positioning math knows
// the bounding box up-front.

export const DEFAULT_SIZES = {
  text: { w: 240, h: 200 },
  // Bigger than the bare-link size so the og:image preview has room to show
  // when present. Without an og:image the flex-1 spacer collapses and the
  // card visually feels appropriately shorter.
  url: { w: 320, h: 260 },
  color: { w: 200, h: 200 },
  image: { w: 280, h: 240 },
  pdf: { w: 240, h: 320 },
  video: { w: 320, h: 180 },
  font: { w: 320, h: 160 },
  // Text-like files (txt / html / markdown / json / csv / source code).
  textFile: { w: 280, h: 320 },
  // Audio players don't need vertical space — just the controls strip.
  audio: { w: 320, h: 100 },
  // Fallback for file items whose MIME type doesn't match a more specific renderer.
  fileOther: { w: 280, h: 200 },
  // Compact card for a live connector (GitHub repo, etc.) — just an icon + target.
  connector: { w: 280, h: 110 },
  // The connector DRAFT is taller: it holds a searchable repo list before commit.
  connectorDraft: { w: 340, h: 400 },
} as const

// Compute the world-space center of the current viewport — a good default spawn point
// for items added via the toolbar (no drop/paste location available).
export function viewportCenterWorld(args: {
  viewport: { width: number; height: number }
  scale: number
  offset: Point
}): Point {
  return screenToWorld(
    { x: args.viewport.width / 2, y: args.viewport.height / 2 },
    { scale: args.scale, offset: args.offset },
  )
}

// Translate a "center" world point + a target size into the top-left position used by
// items. We position by top-left because that's how absolute positioning works in DOM.
export function centerToTopLeft(center: Point, size: { w: number; h: number }): Point {
  return { x: center.x - size.w / 2, y: center.y - size.h / 2 }
}

// MIME types we render as inline text. Covers obvious code / data formats too —
// rendering them as source is more useful than the fallback icon.
const TEXT_LIKE_MIMES = new Set([
  'application/json',
  'application/javascript',
  'application/xml',
  'application/x-yaml',
  'application/x-sh',
])

export function isTextLikeMime(mime: string): boolean {
  if (!mime) return false
  if (mime.startsWith('text/')) return true
  return TEXT_LIKE_MIMES.has(mime)
}

// Markdown is often served as text/plain (or no type at all), so the extension
// is the reliable signal.
export function isMarkdownFile(mime: string, name = ''): boolean {
  if (mime === 'text/markdown' || mime === 'text/x-markdown') return true
  return /\.(md|markdown|mdx)$/i.test(name)
}

// Choose the right default size for a file. Falls back to a generic file box.
export function sizeForFileMime(mime: string, name = ''): { w: number; h: number } {
  if (mime.startsWith('image/')) return DEFAULT_SIZES.image
  if (mime === 'application/pdf') return DEFAULT_SIZES.pdf
  if (mime.startsWith('video/')) return DEFAULT_SIZES.video
  if (mime.startsWith('font/') || mime.includes('font')) return DEFAULT_SIZES.font
  if (mime.startsWith('audio/')) return DEFAULT_SIZES.audio
  if (isMarkdownFile(mime, name) || isTextLikeMime(mime)) return DEFAULT_SIZES.textFile
  return DEFAULT_SIZES.fileOther
}

// Item-construction helpers. Each returns a fully-formed MoodboardItem ready to be
// appended to a pile and sent over tRPC.

export function makeTextItem(args: {
  text: string
  kind: 'reference' | 'instruction'
  position: Point
}): MoodboardItem {
  return {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    type: 'text',
    kind: args.kind,
    text: args.text,
    position: args.position,
    size: DEFAULT_SIZES.text,
  }
}

// Pick a default size for a URL item. Plain URLs use the link-card size; embeddable
// URLs (YouTube / Vimeo / Spotify / Pinterest) get an aspect-ratio-aware size so the
// embed isn't squashed on first paint.
export function sizeForUrl(url: string): { w: number; h: number } {
  const embed = classifyUrl(url)
  if (embed.kind === 'plain') return DEFAULT_SIZES.url
  // Anchor the height at a comfortable card height and derive width from the ratio.
  // Cap width so an embedded video doesn't take over the canvas at low zoom.
  const baseH = embed.kind === 'spotify' ? 80 : 220
  const w = Math.min(420, Math.round(baseH * embed.aspectRatio))
  return { w, h: baseH }
}

export function makeUrlItem(args: { url: string; position: Point }): MoodboardItem {
  return {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    type: 'url',
    kind: 'reference',
    url: args.url,
    position: args.position,
    size: sizeForUrl(args.url),
  }
}

export function makeColorItem(args: {
  hex: string
  label?: string
  position: Point
  id?: string
}): MoodboardItem {
  return {
    id: args.id ?? crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    type: 'color',
    kind: 'reference',
    hex: args.hex,
    label: args.label,
    position: args.position,
    size: DEFAULT_SIZES.color,
  }
}

/** Every provider a moodboard connector can point at, straight from the backend schema. */
export type ConnectorProvider = Extract<MoodboardItem, { type: 'connector' }>['provider']

export function makeConnectorItem(args: {
  provider: ConnectorProvider
  target: string
  label?: string
  position: Point
}): MoodboardItem {
  return {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    type: 'connector',
    kind: 'reference',
    provider: args.provider,
    target: args.target,
    ...(args.label ? { label: args.label } : {}),
    position: args.position,
    size: DEFAULT_SIZES.connector,
  }
}

export function makeFileItem(args: {
  file: {
    source: 'remote'
    url: string
    key: string
    file: { name: string; type: string; size: number }
  }
  position: Point
  // Optional pre-rendered preview image URL (currently used for PDFs).
  thumbnailUrl?: string
  // Optional parsed font metadata (font files only).
  fontMeta?: FontMeta
  // Optional override for the item's persisted size. Callers that have already
  // computed an aspect-ratio-aware size (e.g. via `readImageDimensions` for
  // image uploads) pass it here so the persisted item matches the pending card.
  size?: { w: number; h: number }
}): MoodboardItem {
  const size = args.size ?? sizeForFileMime(args.file.file.type, args.file.file.name)
  return {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    type: 'file',
    kind: 'reference',
    file: args.file,
    position: args.position,
    size,
    ...(args.thumbnailUrl ? { thumbnailUrl: args.thumbnailUrl } : {}),
    ...(args.fontMeta ? { fontMeta: args.fontMeta } : {}),
  }
}
